import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  useCreatePreselectedVoice,
  useDeletePreselectedVoice,
  usePreselectedVoiceList,
  useUpdatePreselectedVoice,
} from "@/hooks/voice/usePreselectedVoices";
import { PreselectedVoice, PreselectedVoiceInput } from "@/types/voice/preselected-voice";
import { PreselectedVoiceDrawer } from "./PreselectedVoiceDrawer";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const emptyInput = (): PreselectedVoiceInput => ({
  voice_id: "",
  label: "",
  description: "",
  gender: "male",
  sort_order: 0,
  is_active: true,
});

const PreselectedVoicesPage = () => {
  const { data: voices = [], isLoading } = usePreselectedVoiceList();
  const createVoice = useCreatePreselectedVoice();
  const updateVoice = useUpdatePreselectedVoice();
  const deleteVoice = useDeletePreselectedVoice();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingVoice, setEditingVoice] = useState<PreselectedVoice | null>(null);
  const [form, setForm] = useState<PreselectedVoiceInput>(emptyInput());

  const openCreate = () => {
    setEditingVoice(null);
    setForm(emptyInput());
    setSheetOpen(true);
  };

  const openEdit = (voice: PreselectedVoice) => {
    setEditingVoice(voice);
    setForm({
      voice_id: voice.voice_id,
      label: voice.label,
      description: voice.description ?? "",
      gender: voice.gender,
      sort_order: voice.sort_order,
      is_active: voice.is_active,
    });
    setSheetOpen(true);
  };

  const handleDelete = (voice: PreselectedVoice) => {
    deleteVoice.mutate(voice.id, {
      onSuccess: () => toast.success(`"${voice.label}" removed`),
      onError: () => toast.error("Failed to remove voice"),
    });
  };

  const handleSave = () => {
    if (!form.voice_id.trim() || !form.label.trim()) {
      toast.error("Voice ID and label are required");
      return;
    }

    const input: PreselectedVoiceInput = {
      ...form,
      description: form.description || undefined,
    };

    if (editingVoice) {
      updateVoice.mutate(
        { id: editingVoice.id, input },
        {
          onSuccess: () => {
            toast.success("Voice updated");
            setSheetOpen(false);
          },
          onError: () => toast.error("Failed to update voice"),
        },
      );
    } else {
      createVoice.mutate(input, {
        onSuccess: () => {
          toast.success("Voice added");
          setSheetOpen(false);
        },
        onError: () => toast.error("Failed to add voice"),
      });
    }
  };

  const isSaving = createVoice.isPending || updateVoice.isPending;

  return (
    <div className="min-h-screen bg-genie-background p-4 md:p-8">
      <div className="max-w-[1920px] mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <GenieIcon name="microphone" size={32} className="h-8 w-8" />
          <div>
            <h1 className="text-3xl font-bold text-genie-text-primary">Preselected Voices</h1>
            <p className="text-genie-text-secondary">Curated voices offered to users when configuring a genie's voice</p>
          </div>
          <Button
            onClick={openCreate}
            className="ml-auto gap-1.5 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] hover:brightness-105 text-white border-0"
          >
            <Plus className="w-4 h-4" />
            Add Voice
          </Button>
        </div>

        <div className="bg-white rounded-lg border border-genie-border">
          {isLoading ? (
            <div className="text-sm text-genie-text-secondary py-12 text-center">Loading...</div>
          ) : voices.length === 0 ? (
            <div className="text-center py-16 text-genie-text-secondary">
              <p className="font-medium text-genie-text-primary mb-1">No preselected voices yet</p>
              <p className="text-sm">Add a voice to start building the curated list.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Label</TableHead>
                    <TableHead>Gender</TableHead>
                    <TableHead>Sort Order</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {voices.map((voice) => (
                    <TableRow key={voice.id}>
                      <TableCell>
                        <div className="font-medium text-sm text-genie-text-primary">{voice.label}</div>
                        {voice.description && (
                          <div className="text-xs text-genie-text-secondary mt-0.5 line-clamp-1">{voice.description}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-genie-primary/10 text-genie-primary border-genie-primary/20 capitalize">
                          {voice.gender}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-genie-text-secondary">{voice.sort_order}</TableCell>
                      <TableCell>
                        <Badge
                          className={
                            voice.is_active
                              ? "bg-green-100 text-green-700 border-green-200"
                              : "bg-genie-background-muted text-genie-text-secondary border-genie-border"
                          }
                        >
                          {voice.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(voice)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(voice)}
                            disabled={deleteVoice.isPending}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <PreselectedVoiceDrawer
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        editingVoice={editingVoice}
        form={form}
        setForm={setForm}
        onSave={handleSave}
        isSaving={isSaving}
      />
    </div>
  );
};

export default PreselectedVoicesPage;
